/**
 * D5 conflict predicate for FacebookPixelEditor's "Events sent to Meta" warning.
 *
 * True when a sibling Custom Script integration is already forwarding the same Meta
 * standard events (silent double-counting risk if both the Facebook Pixel integration's
 * standard-event mode AND the Custom Script's own mapper send them).
 *
 * @module analytics-settings/components/platform-editors
 */

import type { AnalyticsIntegration, CustomScriptConfig } from '../../types/analytics.types';
import { isCustomScriptConfig } from '../../types/analytics.types';

/** The four Meta standard events the built-in Facebook Pixel adapter maps to (see FacebookPixelEventMode). */
const STANDARD_EVENT_NAMES = [
  'ViewContent',
  'CompleteRegistration',
  'InitiateCheckout',
  'AddPaymentInfo',
] as const;

/**
 * True when `siblings` contains an enabled `custom_script` integration whose combined
 * head/body script calls `customEventTracker` for at least one of the four Meta standard
 * event names.
 */
export function hasCustomScriptMetaMapper(siblings: AnalyticsIntegration[] | undefined): boolean {
  if (!siblings || siblings.length === 0) return false;

  return siblings.some((integration) => {
    if (integration.platform !== 'custom_script') return false;
    if (integration.enabled === false) return false;
    if (!isCustomScriptConfig(integration.config)) return false;

    const { bodyScript, headScript } = integration.config as CustomScriptConfig;
    const script = `${bodyScript ?? ''}${headScript ?? ''}`;
    if (!script.includes('customEventTracker')) return false;

    return STANDARD_EVENT_NAMES.some((name) => script.includes(name));
  });
}
